/**
 * useLiveMonitor — cashier-side heartbeat hook.
 *
 * Drop this into the POS Sales page (or any other cashier-facing page) and
 * it'll keep the admin's Live Monitor up to date by:
 *   • posting a heartbeat every 4s with the current cart + customer + last
 *     action,
 *   • polling for any admin message addressed to this session every 5s and
 *     surfacing it as a toast,
 *   • cleanly disconnecting on tab close so the till disappears from the
 *     monitor instead of timing out.
 *
 * The session id is generated once per tab and survives soft reloads via
 * sessionStorage, so a quick refresh on the same till is still recognised
 * as the same session by the admin view.
 */
import { useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const HEARTBEAT_INTERVAL_MS = 4000;
const MESSAGE_POLL_INTERVAL_MS = 5000;
const SESSION_KEY = "live_pos_session_id";

const ensureSessionId = () => {
  let id = sessionStorage.getItem(SESSION_KEY);
  if (!id) {
    id = `till-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    sessionStorage.setItem(SESSION_KEY, id);
  }
  return id;
};

const buildHeartbeat = (state) => {
  const cart = Array.isArray(state.cart) ? state.cart : [];
  const cartItems = cart.slice(0, 30).map((line) => ({
    name: line.name || line.product_name || "Item",
    qty: Number(line.quantity || line.qty || 0),
    line_total: Number(line.total || line.line_total || 0),
  }));
  const cartTotal = cart.reduce(
    (sum, line) => sum + Number(line.total || line.line_total || 0),
    0,
  );
  return {
    session_id: ensureSessionId(),
    cart_items: cartItems,
    cart_total: Math.round(cartTotal * 100) / 100,
    cart_count: cart.length,
    customer_id: state.customerId || null,
    customer_name: state.customerName || null,
    last_action: state.lastAction || null,
    page: state.page || "Sales",
  };
};

export const useLiveMonitor = (enabled, getState) => {
  // Always keep a fresh reference to the state-builder so the interval ticks
  // off the latest cart / customer values without re-creating the interval.
  const getStateRef = useRef(getState);
  useEffect(() => {
    getStateRef.current = getState;
  }, [getState]);

  useEffect(() => {
    if (!enabled) return undefined;
    const sessionId = ensureSessionId();
    let isCancelled = false;

    const sendHeartbeat = async () => {
      if (isCancelled) return;
      try {
        const state = getStateRef.current ? getStateRef.current() : {};
        const payload = buildHeartbeat(state || {});
        await axios.post(`${API}/api/live/heartbeat`, payload, { timeout: 6000 });
      } catch {
        // Silent fail — the heartbeat is best-effort, the cashier shouldn't
        // see toasts for monitor connectivity issues.
      }
    };

    const pollMessages = async () => {
      if (isCancelled) return;
      try {
        const res = await axios.get(`${API}/api/live/message`, {
          params: { session_id: sessionId },
          timeout: 6000,
        });
        const msg = res.data?.message;
        if (msg && msg.message) {
          toast.info(`Message from ${msg.from_user || "Admin"}`, {
            description: msg.message,
            duration: 20000,
          });
        }
      } catch {
        // Silent — see above.
      }
    };

    const disconnect = () => {
      // navigator.sendBeacon survives the page-close lifecycle better than fetch.
      try {
        const blob = new Blob(
          [JSON.stringify({ session_id: sessionId })],
          { type: "application/json" },
        );
        navigator.sendBeacon?.(`${API}/api/live/disconnect`, blob);
      } catch {
        // ignore
      }
    };

    // Fire one immediately so the till shows up in the monitor without a 4s lag.
    sendHeartbeat();
    pollMessages();

    const heartbeatTimer = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);
    const messageTimer = setInterval(pollMessages, MESSAGE_POLL_INTERVAL_MS);
    window.addEventListener("beforeunload", disconnect);

    return () => {
      isCancelled = true;
      clearInterval(heartbeatTimer);
      clearInterval(messageTimer);
      window.removeEventListener("beforeunload", disconnect);
      disconnect();
    };
  }, [enabled]);
};
